/**
 * Ce qu'une facture a reçu, ce qu'elle a été créditée, et qui l'a décidé.
 *
 * ## Pourquoi ce composant existe, et pas `InvoicePaymentSummary`
 *
 * La passation range `InvoicePaymentSummary` parmi les composants à réutiliser
 * (§4.2). Il ne l'est pas : il appelle le RPC `get_invoice_payment_summary`,
 * qui somme **`bank_transactions`** — la table gelée depuis le 27 juillet 2025.
 *
 * Posé tel quel dans l'écran fusionné, il rejouerait à l'intérieur du panneau
 * exactement le mensonge que l'écran est fait pour supprimer :
 *
 * | | l'en-tête dirait | le panneau dirait |
 * | --- | --- | --- |
 * | 2 020 factures | « soldée » | « aucun paiement » |
 *
 * Mesure du 10 septembre 2026 : sur les 2 020 factures marquées `pending`
 * alors qu'elles sont soldées, **2 020 ont leur argent visible dans le nouveau
 * circuit et 18 dans l'ancien**. Le composant existant regarde le mauvais
 * tuyau, et il porte en plus un bouton « Réconcilier » sur `reconcile_invoice`,
 * du même circuit — un bouton qui promet une réparation qu'il ne fait pas.
 *
 * Il n'est pas modifié : les deux anciens écrans continuent de s'en servir le
 * temps qu'on compare les deux affichages (§6.1 point 3). Il partira avec eux.
 *
 * ## D'où viennent les mouvements
 *
 * De `v_reconciliation_journal`, filtrée sur `piece_numero`. Cette vue existe
 * depuis le 7 septembre et porte déjà les deux sens, la méthode d'affectation
 * et la contrepartie. Vérification faite en production avant de l'utiliser :
 * filtrée ainsi, elle reconstitue `allocated_cents` **au centime sur les 2 468
 * factures qui ont reçu de l'argent** — 2 468 retrouvées sur 2 468, aucun
 * écart. Le panneau ne peut donc pas contredire l'en-tête.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle, ArrowDownLeft, ArrowUpRight, FileDown, FileText, Loader2, Receipt, Send,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { messageDeFonction, messageErreur } from '../../../lib/payments/erreur';
import { chargerMouvements, chargerNotesDeCredit } from '../../../lib/factures/gateway.supabase';
import { envoyerNoteDeCredit, MESSAGE_FAMILLE_MAX } from '../../../lib/creditNotes/gateway.supabase';
import {
  ETATS, METHODES, RECLAMATIONS, empechementNoteDeCredit, reclamation,
  type LigneFacture, type Mouvement, type NoteDeCredit,
} from '../../../lib/factures/types';
import { dateCourte, euros } from './money';

interface Props {
  facture: LigneFacture;
  /** Rappelé après une action qui change la facture, pour recharger la liste. */
  onChange: () => void;
}

export default function DetailFacture({ facture, onChange }: Props) {
  const [notes, setNotes] = useState<NoteDeCredit[]>([]);
  const [mouvements, setMouvements] = useState<Mouvement[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [pdfEnCours, setPdfEnCours] = useState<string | null>(null);
  /* La note dont le formulaire d'envoi est ouvert, et ce qu'on y écrit. */
  const [envoiOuvert, setEnvoiOuvert] = useState<string | null>(null);
  const [messageEnvoi, setMessageEnvoi] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [parFacture, mvts] = await Promise.all([
        chargerNotesDeCredit([facture.invoiceId]),
        chargerMouvements(facture.invoiceNumber),
      ]);
      setNotes(parFacture.get(facture.invoiceId) ?? []);
      setMouvements(mvts);
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture du détail impossible'));
    } finally {
      setChargement(false);
    }
  }, [facture.invoiceId, facture.invoiceNumber]);

  useEffect(() => { void charger(); }, [charger]);

  /*
    Produire le PDF d'une note de crédit qui n'en a pas.

    34 notes sont dans ce cas : 33 de l'ancien circuit, et celles dont la
    génération avait échoué — la fonction levait une exception sur 43 lignes de
    la base, faute de savoir imprimer un nom polonais ou roumain. La note
    existait, son document non, et rien ne permettait de le réclamer sans
    réémettre la note.
  */
  const genererPdf = async (creditNoteId: string, numero: string) => {
    setPdfEnCours(creditNoteId);
    try {
      const { data, error } = await supabase.functions.invoke('generate-credit-note-pdf', {
        body: { credit_note_id: creditNoteId },
      });
      if (error) throw error;
      const r = (data ?? {}) as Record<string, unknown>;
      if (typeof r.error === 'string') throw new Error(r.error);
      toast.success(`PDF de ${numero} produit`);
      await charger();
      onChange();
    } catch (e) {
      /*
        `messageDeFonction` et non `messageErreur` : une Edge Function met sa
        raison dans le CORPS de la réponse, et `invoke` ne la remonte pas.
        Sans cette lecture, un refus d'autorisation (403) et une session
        expirée s'affichent tous deux « Edge Function returned a non-2xx
        status code » — le cas mesuré sur NC-26-00515 le 10 septembre 2026.
      */
      toast.error(await messageDeFonction(e, `PDF de ${numero} impossible`));
    } finally {
      setPdfEnCours(null);
    }
  };

  /*
    Envoyer — ou renvoyer — une note de crédit à la famille.

    Depuis le 4 septembre 2026, la modale d'émission n'envoyait plus rien, et
    aucun écran ne permettait de rattraper : 34 notes réelles ne sont jamais
    parties. Ce bouton est le rattrapage, et aussi le « pas maintenant » de la
    modale. Le formulaire montre l'adresse avant le clic : c'est lui, la
    confirmation.
  */
  const ouvrirEnvoi = (creditNoteId: string) => {
    setEnvoiOuvert((ouvert) => (ouvert === creditNoteId ? null : creditNoteId));
    setMessageEnvoi('');
  };

  const envoyerLaNote = async (n: NoteDeCredit) => {
    setEnvoiEnCours(n.creditNoteId);
    const r = await envoyerNoteDeCredit(n.creditNoteId, messageEnvoi);
    setEnvoiEnCours(null);
    if (!r.envoyeA) {
      toast.error(`${n.numero} n'est pas partie : ${r.erreur}`, { duration: 10000 });
      return;
    }
    toast.success(`${n.numero} envoyée à ${r.envoyeA}.`);
    setEnvoiOuvert(null);
    setMessageEnvoi('');
    // Le statut est passé à « envoyée », et le PDF a été régénéré.
    await charger();
  };

  const etat = ETATS[facture.settlement];
  const quoi = reclamation(facture);
  const marque = quoi === 'note_a_emettre' || quoi === 'annulation_seule'
    ? RECLAMATIONS[quoi]
    : null;
  const empechement = empechementNoteDeCredit(facture);

  return (
    <div className="space-y-5 bg-gray-50 px-6 py-5">
      {/* --- Ce que la facture réclame, et pourquoi ------------------------ */}
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Le compte de la facture
        </h4>
        <p className="mb-3 text-sm text-gray-600">{etat.phrase}</p>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-5">
          <Montant terme="Facturé" valeur={facture.factureCents} />
          <Montant
            terme="Notes de crédit"
            valeur={-facture.notesCents}
            detail={facture.notesCount > 0
              ? `${facture.notesCount} note${facture.notesCount > 1 ? 's' : ''}`
              : 'aucune'}
          />
          <Montant terme="Net réclamé" valeur={facture.netCents} fort />
          <Montant
            terme="Reçu"
            valeur={facture.recuCents}
            detail={facture.dernierPaiement
              ? `dernier le ${dateCourte(facture.dernierPaiement)}`
              : undefined}
          />
          <Montant terme="Reste dû" valeur={facture.resteCents} fort />
        </dl>

        {/*
          Le trop-perçu, dit seulement quand il existe. C'est la trace d'une
          note de crédit émise sans déplacer l'argent qu'elle a libéré : la
          passation appelle les deux gestes « un appel, pas deux ».
        */}
        {facture.tropPercuCents > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <strong>{euros(facture.tropPercuCents)} de trop-perçu.</strong> La facture
              porte plus d’argent que son net. C’est ce qui arrive quand une note de crédit
              est émise sans déplacer en provision ce qu’elle libère — les deux gestes n’en
              font qu’un.
            </span>
          </p>
        )}

        {/*
          Le solde résiduel d'une facture annulée, dit pour ce qu'il est.

          Ne pas faire disparaître la contradiction (§5.6) — mais ne pas
          l'appeler une créance non plus. Les deux cas ont chacun leur phrase,
          et quand la base empêche le geste, elle le dit ici plutôt que de
          laisser un bouton gris pour seule explication.
        */}
        {marque && (
          <div className="mt-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
            <p className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>{euros(facture.resteCents)}</strong> restent inscrits, et ne sont
                réclamés à personne. {marque.phrase}
              </span>
            </p>
            {empechement && (
              <p className="mt-2 pl-6 text-xs text-gray-600">{empechement}</p>
            )}
          </div>
        )}
      </div>

      {/* --- Le document ------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-gray-200 pt-4 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Le document
        </span>
        {facture.pdfUrl ? (
          <a
            href={facture.pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-indigo-700 underline decoration-dotted underline-offset-2 hover:text-indigo-900"
          >
            <FileText className="h-4 w-4" /> Ouvrir le PDF
          </a>
        ) : (
          <span className="text-gray-500">Aucun PDF enregistré</span>
        )}
        <span className="text-gray-600">
          Courriel :{' '}
          {facture.courrielEnvoyeLe
            ? `parti le ${dateCourte(facture.courrielEnvoyeLe)}`
            : 'jamais réclamé'}
        </span>
        {facture.communicationStructuree && (
          <span className="font-mono text-xs text-gray-600">
            +++{facture.communicationStructuree.slice(0, 3)}/
            {facture.communicationStructuree.slice(3, 7)}/
            {facture.communicationStructuree.slice(7)}+++
          </span>
        )}
      </div>

      {chargement ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Lecture du détail…
        </div>
      ) : erreur ? (
        <p className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {erreur}
        </p>
      ) : (
        <>
          {/* --- Les notes de crédit émises ----------------------------- */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Notes de crédit
            </h4>
            {notes.length === 0 ? (
              <p className="text-sm text-gray-500">Aucune note de crédit sur cette facture.</p>
            ) : (
              <ul className="space-y-1">
                {notes.map((n) => (
                  <li key={n.creditNoteId} className="text-sm">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Receipt className="h-4 w-4 shrink-0 text-indigo-600" />
                      <span className="font-mono text-xs font-semibold text-gray-900">{n.numero}</span>
                      <span className="tabular-nums text-gray-900">{euros(n.montantCents)}</span>
                      <span className="text-gray-500">{dateCourte(n.emiseLe)}</span>
                      {n.motif && <span className="text-xs text-gray-500">{MOTIFS[n.motif] ?? n.motif}</span>}
                      <span className={clsx(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        n.statut === 'sent' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600',
                      )}>
                        {n.statut === 'sent' ? 'envoyée' : 'jamais envoyée'}
                      </span>
                      {n.pdfUrl ? (
                        <a
                          href={n.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={`Ouvrir la note de crédit ${n.numero}`}
                          className="inline-flex items-center gap-1 text-indigo-700 underline decoration-dotted underline-offset-2 hover:text-indigo-900"
                        >
                          <FileText className="h-3.5 w-3.5" /> PDF
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => genererPdf(n.creditNoteId, n.numero)}
                          disabled={pdfEnCours === n.creditNoteId}
                          title="Produire le PDF de cette note de crédit"
                          className="inline-flex items-center gap-1 text-gray-600 hover:text-indigo-700 disabled:opacity-50"
                        >
                          {pdfEnCours === n.creditNoteId
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <FileDown className="h-3.5 w-3.5" />}
                          Produire le PDF
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => ouvrirEnvoi(n.creditNoteId)}
                        aria-expanded={envoiOuvert === n.creditNoteId}
                        title={n.statut === 'sent'
                          ? 'Renvoyer cette note de crédit à la famille'
                          : 'Envoyer cette note de crédit à la famille'}
                        className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900"
                      >
                        <Send className="h-3.5 w-3.5" />
                        {n.statut === 'sent' ? 'Renvoyer' : 'Envoyer'}
                      </button>
                      {n.notes && <span className="text-xs italic text-gray-500">« {n.notes} »</span>}
                    </div>

                    {envoiOuvert === n.creditNoteId && (
                      <div className="mt-2 ml-7 max-w-xl space-y-2 rounded-md border border-gray-200 bg-white p-3">
                        {facture.courriel ? (
                          <>
                            <label htmlFor={`message-${n.creditNoteId}`} className="block text-sm text-gray-700">
                              Message à la famille (facultatif, ajouté au courriel)
                            </label>
                            <textarea
                              id={`message-${n.creditNoteId}`}
                              value={messageEnvoi}
                              onChange={(e) => setMessageEnvoi(e.target.value)}
                              rows={3}
                              maxLength={MESSAGE_FAMILLE_MAX}
                              className="form-input text-sm"
                              placeholder="Par exemple : le tarif réduit a été appliqué, comme convenu."
                            />
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={() => envoyerLaNote(n)}
                                disabled={envoiEnCours === n.creditNoteId}
                                className="btn-primary inline-flex items-center px-3 py-1.5 text-sm disabled:opacity-50"
                              >
                                {envoiEnCours === n.creditNoteId
                                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  : <Send className="mr-2 h-4 w-4" />}
                                Envoyer à {facture.courriel}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEnvoiOuvert(null)}
                                disabled={envoiEnCours === n.creditNoteId}
                                className="text-sm text-gray-600 hover:text-gray-900"
                              >
                                Annuler
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              Le PDF est produit à nouveau avant l'envoi, avec la situation de la
                              facture à ce jour.
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-amber-800">
                            Cette famille n'a pas d'adresse courriel : la note ne peut pas être envoyée.
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* --- Les mouvements d'argent -------------------------------- */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Mouvements d’argent
            </h4>
            {mouvements.length === 0 ? (
              <p className="text-sm text-gray-500">
                Aucun mouvement affecté à cette facture.
                {facture.settlement === 'unpaid' && (
                  <>
                    {' '}Le rapprochement CODA est en cours : tous les relevés ne sont pas
                    encore importés, donc une facture sans paiement visible n’est pas
                    forcément une facture impayée.
                  </>
                )}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                      <th className="py-1 pr-4 font-medium">Date</th>
                      <th className="py-1 pr-4 font-medium">Montant</th>
                      <th className="py-1 pr-4 font-medium">Décidé par</th>
                      <th className="py-1 pr-4 font-medium">Communication</th>
                      <th className="py-1 pr-4 font-medium">Contrepartie</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {mouvements.map((m) => {
                      const methode = METHODES[m.methode] ?? {
                        label: m.methode, classe: 'bg-gray-100 text-gray-700',
                      };
                      return (
                        <tr key={m.ligneId}>
                          <td className="py-1.5 pr-4 whitespace-nowrap text-gray-900">
                            {dateCourte(m.dateValeur)}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            <span className={clsx(
                              'inline-flex items-center gap-1 tabular-nums font-medium',
                              m.sens === 'entree' ? 'text-green-800' : 'text-orange-800',
                            )}>
                              {m.sens === 'entree'
                                ? <ArrowDownLeft className="h-3.5 w-3.5" />
                                : <ArrowUpRight className="h-3.5 w-3.5" />}
                              {euros(m.montantCents)}
                            </span>
                            {/*
                              Le mouvement bancaire complet, quand l'affectation
                              n'en couvre qu'une part : un virement de 300 EUR
                              réparti sur trois factures ne doit pas laisser
                              croire que 100 EUR seulement sont arrivés.
                            */}
                            {m.mouvementCents !== m.montantCents && (
                              <span className="ml-1 text-xs text-gray-500">
                                sur {euros(m.mouvementCents)}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            <span className={clsx(
                              'rounded-full px-2 py-0.5 text-xs font-medium',
                              methode.classe,
                            )}>
                              {methode.label}
                            </span>
                          </td>
                          <td className="py-1.5 pr-4 font-mono text-xs text-gray-600">
                            {m.communication ?? '—'}
                          </td>
                          <td className="py-1.5 pr-4 text-gray-600">
                            {m.nomContrepartie ?? '—'}
                            {m.compteContrepartie && (
                              <span className="ml-1 font-mono text-xs text-gray-500">
                                {m.compteContrepartie}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Les motifs d'une note de crédit, en français. */
const MOTIFS: Record<string, string> = {
  tariff_correction: 'correction de tarif',
  cancellation: 'annulation',
  absence: 'absence',
  other: 'autre',
};

/** Un terme du compte de la facture, et son montant. */
function Montant({
  terme, valeur, detail, fort = false,
}: {
  terme: string;
  valeur: number;
  detail?: string;
  fort?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{terme}</dt>
      <dd className={clsx(
        'tabular-nums',
        fort ? 'font-semibold text-gray-900' : 'text-gray-800',
      )}>
        {euros(valeur)}
      </dd>
      {detail && <dd className="text-xs text-gray-500">{detail}</dd>}
    </div>
  );
}
